'use client'

import { useEffect, useState } from 'react'

interface RadarCategory {
  label: string
  score: number
  maxScore: number
  color: string
}

interface ReadinessRadarProps {
  categories: RadarCategory[]
}

const AXES = 5
const CENTER = 150
const RADIUS = 110
const LABEL_RADIUS = 135

function polarToCartesian(
  cx: number,
  cy: number,
  r: number,
  index: number,
  total: number
): { x: number; y: number } {
  const angle = (2 * Math.PI * index) / total - Math.PI / 2
  return {
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  }
}

export default function ReadinessRadar({ categories }: ReadinessRadarProps) {
  const [animatedPcts, setAnimatedPcts] = useState<number[]>(
    categories.map(() => 0)
  )

  useEffect(() => {
    const targetPcts = categories.map(c =>
      c.maxScore > 0 ? c.score / c.maxScore : 0
    )
    const timer = setTimeout(() => {
      setAnimatedPcts(targetPcts)
    }, 200)
    return () => clearTimeout(timer)
  }, [categories])

  // Generate concentric pentagon outlines (20%, 40%, 60%, 80%, 100%)
  const levels = [0.2, 0.4, 0.6, 0.8, 1.0]
  const levelPaths = levels.map(level => {
    const points = Array.from({ length: AXES }, (_, i) => {
      const pt = polarToCartesian(CENTER, CENTER, RADIUS * level, i, AXES)
      return `${pt.x},${pt.y}`
    })
    return points.join(' ')
  })

  // Axis lines
  const axisLines = Array.from({ length: AXES }, (_, i) =>
    polarToCartesian(CENTER, CENTER, RADIUS, i, AXES)
  )

  // Score polygon
  const scorePoints = animatedPcts.map((pct, i) => {
    const pt = polarToCartesian(
      CENTER,
      CENTER,
      RADIUS * Math.max(pct, 0.05),
      i,
      AXES
    )
    return `${pt.x},${pt.y}`
  })

  // Label positions
  const labelPositions = categories.map((cat, i) =>
    polarToCartesian(CENTER, CENTER, LABEL_RADIUS, i, AXES)
  )

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      <h2 className='text-lg font-semibold text-foreground mb-4 text-center'>
        Readiness Radar
      </h2>
      <div className='flex justify-center'>
        <svg
          viewBox='-50 -10 400 320'
          className='w-full max-w-[320px] h-auto'
          role='img'
          aria-label='AI Readiness radar chart showing scores across 5 categories'
        >
          {/* Concentric pentagon outlines */}
          {levelPaths.map((pts, i) => (
            <polygon
              key={i}
              points={pts}
              fill='none'
              stroke='var(--border)'
              strokeWidth={i === levels.length - 1 ? 1.5 : 0.5}
              opacity={0.4}
            />
          ))}

          {/* Axis lines */}
          {axisLines.map((pt, i) => (
            <line
              key={i}
              x1={CENTER}
              y1={CENTER}
              x2={pt.x}
              y2={pt.y}
              stroke='var(--border)'
              strokeWidth={0.5}
              opacity={0.3}
            />
          ))}

          {/* Score polygon fill */}
          <polygon
            points={scorePoints.join(' ')}
            fill='url(#radarGradient)'
            stroke='none'
            opacity={0.25}
            style={{ transition: 'all 0.8s ease-out' }}
          />

          {/* Score polygon border */}
          <polygon
            points={scorePoints.join(' ')}
            fill='none'
            stroke='var(--primary)'
            strokeWidth={2}
            opacity={0.6}
            style={{ transition: 'all 0.8s ease-out' }}
          />

          {/* Score dots at each vertex */}
          {animatedPcts.map((pct, i) => {
            const pt = polarToCartesian(
              CENTER,
              CENTER,
              RADIUS * Math.max(pct, 0.05),
              i,
              AXES
            )
            return (
              <circle
                key={i}
                cx={pt.x}
                cy={pt.y}
                r={4}
                fill={categories[i].color}
                stroke='var(--background)'
                strokeWidth={2}
                style={{ transition: 'all 0.8s ease-out' }}
              />
            )
          })}

          {/* Labels */}
          {labelPositions.map((pos, i) => {
            const cat = categories[i]
            const pct =
              cat.maxScore > 0
                ? Math.round((cat.score / cat.maxScore) * 100)
                : 0
            // Adjust text anchor based on position
            let anchor: 'start' | 'middle' | 'end' = 'middle'
            if (pos.x < CENTER - 20) anchor = 'end'
            else if (pos.x > CENTER + 20) anchor = 'start'

            return (
              <g key={i}>
                <text
                  x={pos.x}
                  y={pos.y - 6}
                  textAnchor={anchor}
                  className='fill-foreground text-[9px] font-semibold'
                >
                  {cat.label}
                </text>
                <text
                  x={pos.x}
                  y={pos.y + 7}
                  textAnchor={anchor}
                  fill={cat.color}
                  className='text-[8px] font-bold'
                >
                  {pct}%
                </text>
              </g>
            )
          })}

          <defs>
            <radialGradient id='radarGradient' cx='50%' cy='50%' r='50%'>
              <stop offset='0%' stopColor='var(--primary)' stopOpacity={0.3} />
              <stop
                offset='100%'
                stopColor='var(--primary)'
                stopOpacity={0.05}
              />
            </radialGradient>
          </defs>
        </svg>
      </div>

      {/* Legend */}
      <div className='flex flex-wrap justify-center gap-3 mt-4'>
        {categories.map(cat => (
          <div key={cat.label} className='flex items-center gap-1.5'>
            <span
              className='h-2 w-2 rounded-full'
              style={{ backgroundColor: cat.color }}
            />
            <span className='text-[10px] text-muted-foreground'>
              {cat.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
